import type { ReactNode } from 'react';
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../components/FeatherIcon';
import { LoadingBlock, Spinner } from '../components/Spinner';
import { canCopyToSend } from './copyToSend';
import { MessageSheet } from './MessageSheet';
import { messageRights } from './messageRights';
import { findQuoted, splitQuote } from './quote';
import { priceState, type ChatMedia, type ChatMessage } from './messages';
import type { Translation } from './useTranslations';
import { playableUrl, useMediaUrl } from './media';
import { useStyling } from '../theme/userStyling';

type MediaView = 'expanded' | 'compact';

interface Props {
  accountId: string;
  fanId: string;
  fanName: string;
  messages: ChatMessage[];
  loading: boolean;
  loadingOlder: boolean;
  hasOlder: boolean;
  error: string | null;
  onLoadOlder: () => void;
  onReply?: (m: ChatMessage) => void;
  /** "Copy to send": the whole message (text, media, price) into the composer. */
  onCopyToSend?: (m: ChatMessage) => void;
  onUnsend?: (messageId: string) => Promise<void>;
  onRetry?: (tempId: string) => void;
  onDiscard?: (tempId: string) => void;
  /** Said when there are no messages (default: the chat is empty). */
  emptyText?: string;
  /** A message to bring into view and mark (Find in chat's current hit). */
  highlightId?: string | null;
  /** Like / take back a like on a fan's message; absent = likes are read-only. */
  onLike?: (messageId: string, liked: boolean) => Promise<void>;
  /** Translations shown under messages, by message id. */
  translations?: Map<string, Translation>;
  /** "Translate" in a message's menu. */
  onTranslate?: (m: ChatMessage) => void;
  /** A fan's message came into view (auto-translation). */
  onSeen?: (m: ChatMessage) => void;
  /** Phones: press and hold a message for its actions (a sheet) instead of the hover menu. */
  holdToAct?: boolean;
}

/** How long a press must hold to open a message's actions (design: 430 ms). */
const HOLD_MS = 430;
/** A finger that moves this far is scrolling, not holding. */
const HOLD_SLOP = 8;
/** Where a hold means the thing under the finger, not the message. */
const NOT_A_HOLD = 'button, a, video, audio, input, textarea';

function dayLabel(ms: number, locale: string): string {
  const d = new Date(ms);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return '';
  if (d.toDateString() === yesterday.toDateString()) return 'yesterday';
  return d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
}

function timeLabel(ms: number, locale: string): string {
  return ms ? new Date(ms).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : '';
}

function money(n: number): string {
  return `$${n.toFixed(2)}`;
}

function Thumb({ accountId, fanId, item }: { accountId: string; fanId: string; item: ChatMedia }) {
  return (
    <div className="rc-thumb">
      <ThumbBody accountId={accountId} fanId={fanId} item={item} />
      {item.preview && <FreePreviewTag />}
    </div>
  );
}

function FreePreviewTag() {
  const { t } = useTranslation('chat');
  return (
    <span className="rc-preview-tag">
      <FeatherIcon name="eye" size={11} />
      {t('thread.freePreview')}
    </span>
  );
}

function ThumbBody({ accountId, fanId, item }: { accountId: string; fanId: string; item: ChatMedia }) {
  const { t } = useTranslation('chat');
  const { url, failed } = useMediaUrl(accountId, item.thumb);
  const [playing, setPlaying] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(false);

  if (!item.canView) {
    return (
      <div className="rc-media rc-media--locked">
        {url && <img src={url} alt="" className="rc-media-blur" />}
        <span className="rc-media-lock">
          <FeatherIcon name="lock" size={16} />
          {t(`media.${item.type}`)}
        </span>
      </div>
    );
  }

  if (item.type === 'audio') {
    return (
      <div className="rc-audio">
        {playing ? (
          <audio src={playing} controls autoPlay />
        ) : (
          <button
            type="button"
            className="rc-audio-play"
            disabled={busy || !item.src}
            onClick={async () => {
              if (!item.src) return;
              setBusy(true);
              try {
                setPlaying(await playableUrl(accountId, fanId, item.src));
              } finally {
                setBusy(false);
              }
            }}
          >
            <FeatherIcon name="play" size={14} />
            {t('media.voice')}
            {item.duration ? <span className="rc-muted">{t('media.seconds', { count: Math.round(item.duration) })}</span> : null}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="rc-media">
      {url ? (
        item.type === 'photo' || item.type === 'gif' || item.type === 'video' ? (
          <button
            type="button"
            className="rc-media-open"
            title={item.type === 'video' ? t('media.play') : t('media.open')}
            disabled={item.type === 'video' && !item.src}
            onClick={() => setViewing(true)}
          >
            <img src={url} alt="" loading="lazy" />
          </button>
        ) : (
          <img src={url} alt="" loading="lazy" />
        )
      ) : item.type === 'video' && item.src ? (
        <button type="button" className="rc-media-open" title={t('media.play')} onClick={() => setViewing(true)}>
          <span className="rc-media-empty">{t('media.video')}</span>
        </button>
      ) : (
        <span className="rc-media-empty">{failed ? t('media.unavailable') : t(`media.${item.type}`)}</span>
      )}
      {item.type === 'video' && (
        <span className="rc-media-play" aria-hidden="true">
          <FeatherIcon name="play" size={18} />
        </span>
      )}
      {viewing &&
        (item.type === 'video' ? (
          item.src && <VideoViewer accountId={accountId} fanId={fanId} src={item.src} onClose={() => setViewing(false)} />
        ) : (
          <Viewer accountId={accountId} url={item.src || item.thumb} onClose={() => setViewing(false)} />
        ))}
    </div>
  );
}

/** "2 photos · 1 video" for a message's media. */
function mediaCountLabel(items: ChatMedia[], t: (key: string, o?: Record<string, unknown>) => string): string {
  let photos = 0;
  let videos = 0;
  let files = 0;
  for (const item of items) {
    if (item.type === 'photo' || item.type === 'gif') photos += 1;
    else if (item.type === 'video') videos += 1;
    else files += 1;
  }
  return [
    photos && t('media.photos', { count: photos }),
    videos && t('media.videos', { count: videos }),
    files && t('media.files', { count: files }),
  ]
    .filter(Boolean)
    .join(' · ');
}

function MiniThumb({ accountId, item }: { accountId: string; item: ChatMedia }) {
  const { url } = useMediaUrl(accountId, item.thumb);
  return (
    <span className={`rc-media-mini ${item.canView ? '' : 'is-locked'}`}>
      {url && <img src={url} alt="" loading="lazy" />}
      {!item.canView ? <FeatherIcon name="lock" size={11} /> : item.type === 'video' ? <FeatherIcon name="play" size={11} /> : null}
    </span>
  );
}

/** Media view Compact: one line with small thumbnails and the count; a click shows it full size. */
function MediaChip({ accountId, items, onExpand }: { accountId: string; items: ChatMedia[]; onExpand: () => void }) {
  const { t } = useTranslation('chat');
  const shown = items.slice(0, 4);
  return (
    <button type="button" className="rc-media-chip" title={t('media.expand')} onClick={onExpand}>
      <span className="rc-media-minis">
        {shown.map((item, idx) => (
          <MiniThumb key={`${item.id}:${idx}`} accountId={accountId} item={item} />
        ))}
        {items.length > shown.length && <span className="rc-media-mini is-more">+{items.length - shown.length}</span>}
      </span>
      <span className="rc-media-chip-label">{mediaCountLabel(items, t)}</span>
      <FeatherIcon name="maximize-2" size={13} />
    </button>
  );
}

function ViewerShell({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const { t } = useTranslation('chat');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  // Outside the bubble: the thumbnail's own sizing (.rc-media img, cropped to 320px) must not reach the full picture.
  return createPortal(
    <div className="rc-viewer" role="dialog" onClick={onClose}>
      {children}
      <button type="button" className="rc-viewer-close" title={t('common.close')} onClick={onClose}>
        <FeatherIcon name="x" size={18} />
      </button>
    </div>,
    document.body,
  );
}

function Viewer({ accountId, url, onClose }: { accountId: string; url: string | null; onClose: () => void }) {
  const { t } = useTranslation('chat');
  const full = useMediaUrl(accountId, url);
  return (
    <ViewerShell onClose={onClose}>
      {full.url ? <img src={full.url} alt="" /> : full.failed ? <span className="rc-muted">{t('media.unavailable')}</span> : <Spinner size={28} label={t('thread.loading')} />}
    </ViewerShell>
  );
}

/** A chat video full screen: a spinner until its first frame is in, then the video at its own size. */
function VideoViewer({ accountId, fanId, src, onClose }: { accountId: string; fanId: string; src: string; onClose: () => void }) {
  const { t } = useTranslation('chat');
  const [playing, setPlaying] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    playableUrl(accountId, fanId, src).then(
      (u) => alive && setPlaying(u),
      () => alive && setFailed(true),
    );
    return () => {
      alive = false;
    };
  }, [accountId, fanId, src]);
  if (failed) {
    return (
      <ViewerShell onClose={onClose}>
        <span className="rc-muted">{t('media.unavailable')}</span>
      </ViewerShell>
    );
  }
  return (
    <ViewerShell onClose={onClose}>
      {!ready && <Spinner size={28} label={t('thread.loading')} />}
      {playing && (
        <video
          className={ready ? '' : 'is-loading'}
          src={playing}
          controls
          autoPlay
          playsInline
          onClick={(e) => e.stopPropagation()}
          onLoadedData={() => setReady(true)}
          onError={() => setFailed(true)}
        />
      )}
    </ViewerShell>
  );
}

function PriceState({ m }: { m: ChatMessage }) {
  const { t } = useTranslation('chat');
  const state = priceState(m);
  if (!state) return null;
  if (state.kind === 'free') {
    return (
      <span className="rc-price-state is-free" title={m.side === 'fan' ? t('thread.fanFreeHint') : t('thread.freeHint')}>
        <FeatherIcon name="gift" size={12} />
        {t('thread.free')}
      </span>
    );
  }
  const unlocked = state.kind === 'unlocked';
  return (
    <span className={`rc-price-state ${unlocked ? 'is-unlocked' : 'is-locked'}`} title={unlocked ? t('thread.unlockedHint') : t('thread.lockedHint')}>
      <FeatherIcon name={unlocked ? 'unlock' : 'lock'} size={12} />
      <b>{money(state.price)}</b>
      <span>· {unlocked ? t('thread.unlocked') : t('thread.locked')}</span>
    </span>
  );
}

function MessageMenu({ m, onReply, onCopyToSend, onUnsend, onLike, onTranslate }: { m: ChatMessage } & Pick<Props, 'onReply' | 'onCopyToSend' | 'onUnsend' | 'onLike' | 'onTranslate'>) {
  const { t } = useTranslation('chat');
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const mine = m.side === 'creator';
  const rights = messageRights(m, { onUnsend, onLike });
  const canUnsend = rights.unsend;
  const canLike = rights.like;

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (box.current?.contains(e.target as Node)) return;
      setOpen(false);
      setConfirming(false);
    };
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [open]);

  const done = () => {
    setOpen(false);
    setConfirming(false);
  };

  return (
    <div className={`rc-msg-menu ${mine ? 'is-mine' : ''} ${open ? 'is-open' : ''}`} ref={box}>
      <button type="button" className="rc-msg-menu-btn" title={t('menu.more')} onClick={() => setOpen((v) => !v)}>
        <FeatherIcon name="more-horizontal" size={14} />
      </button>
      {open && (
        <div className="rc-msg-menu-list" role="menu">
          {confirming ? (
            <div className="rc-msg-menu-confirm">
              <span>{t('menu.unsendConfirm')}</span>
              {failed && <span className="rc-error">{failed}</span>}
              <div>
                <button type="button" onClick={() => setConfirming(false)} disabled={busy}>
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="is-danger"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    setFailed(null);
                    try {
                      await onUnsend!(m.id);
                      done();
                    } catch (err) {
                      setFailed((err as Error).message);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  {busy ? <Spinner size={12} label={t('menu.unsend')} /> : t('menu.unsend')}
                </button>
              </div>
            </div>
          ) : (
            <>
              {m.text && (
                <button type="button" role="menuitem" onClick={() => (void navigator.clipboard?.writeText(splitQuote(m.text).body).catch(() => undefined), done())}>
                  <FeatherIcon name="copy" size={13} />
                  {t('menu.copy')}
                </button>
              )}
              {onReply && (
                <button type="button" role="menuitem" onClick={() => (onReply(m), done())}>
                  <FeatherIcon name="corner-up-left" size={13} />
                  {t('menu.reply')}
                </button>
              )}
              {onCopyToSend && canCopyToSend(m) && (
                <button type="button" role="menuitem" title={t('menu.copyToSendHint')} onClick={() => (onCopyToSend(m), done())}>
                  <FeatherIcon name="clipboard" size={13} />
                  {t('menu.copyToSend')}
                </button>
              )}
              {onTranslate && m.text && (
                <button type="button" role="menuitem" onClick={() => (onTranslate(m), done())}>
                  <FeatherIcon name="globe" size={13} />
                  {t('menu.translate')}
                </button>
              )}
              {canLike && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    done();
                    void onLike!(m.id, true).catch(() => undefined);
                  }}
                >
                  <FeatherIcon name="heart" size={13} />
                  {t('menu.like')}
                </button>
              )}
              {canUnsend && (
                <button type="button" role="menuitem" className="is-danger" onClick={() => setConfirming(true)}>
                  <FeatherIcon name="trash-2" size={13} />
                  {t('menu.unsend')}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function TranslationLine({ tr }: { tr: Translation | undefined }) {
  const { t } = useTranslation('chat');
  if (!tr || (tr.state === 'error' && !tr.manual)) return null;
  return (
    <span className={`rc-translation ${tr.state === 'error' ? 'is-error' : ''}`}>
      {tr.state === 'busy' ? t('translate.busy') : tr.state === 'error' ? t('translate.failed', { error: tr.text }) : tr.text}
    </span>
  );
}

/** The creator's like on a fan's message (chats.js message-like-heart); a click takes it back. */
function LikeHeart({ m, onLike }: { m: ChatMessage } & Pick<Props, 'onLike'>) {
  const { t } = useTranslation('chat');
  const [busy, setBusy] = useState(false);
  if (!onLike || !/^\d+$/.test(m.id)) {
    return (
      <span className="rc-like is-liked" role="img" aria-label={t('menu.liked')} title={t('menu.liked')}>
        <FeatherIcon name="heart" size={13} />
      </span>
    );
  }
  return (
    <button
      type="button"
      className="rc-like is-liked"
      aria-label={t('menu.unlike')}
      title={t('menu.unlike')}
      disabled={busy}
      onClick={() => {
        setBusy(true);
        onLike(m.id, false)
          .catch(() => undefined)
          .finally(() => setBusy(false));
      }}
    >
      <FeatherIcon name="heart" size={13} />
    </button>
  );
}

/** Who sent a creator-side message: You / @teammate / AI; nothing when unknown. */
function SenderChip({ m }: { m: ChatMessage }) {
  const { t } = useTranslation('chat');
  if (m.sender.kind === 'me') return <span className="rc-chip rc-chip--you">{t('sender.you')}</span>;
  if (m.sender.kind === 'teammate') return <span className="rc-chip rc-chip--you">@{m.sender.username}</span>;
  if (m.sender.kind === 'ai')
    return (
      <span className="rc-chip rc-chip--ai">
        {m.sender.via === 'reply' ? t('sender.ai') : t('sender.aiVia', { via: t(`sender.via.${m.sender.via}`) })}
      </span>
    );
  return null;
}

function knownSender(m: ChatMessage): boolean {
  return m.sender.kind === 'me' || m.sender.kind === 'teammate' || m.sender.kind === 'ai';
}

export function Transcript({ accountId, fanId, fanName, messages, loading, loadingOlder, hasOlder, error, onLoadOlder, onReply, onCopyToSend, onUnsend, onRetry, onDiscard, emptyText, highlightId = null, onLike, translations, onTranslate, onSeen, holdToAct = false }: Props) {
  const { t, i18n } = useTranslation('chat');
  const scroller = useRef<HTMLDivElement>(null);
  const topSentinel = useRef<HTMLDivElement>(null);
  const pinnedToBottom = useRef(true);
  const prevHeight = useRef(0);
  const prevFirst = useRef<string | null>(null);
  // Media view (Settings → Styling), and messages flipped the other way in this chat only.
  const { mediaView } = useStyling();
  const [flipped, setFlipped] = useState<Map<string, MediaView>>(() => new Map());
  const viewOf = (id: string): MediaView => flipped.get(id) ?? mediaView;
  const flip = (id: string) =>
    setFlipped((prev) => {
      const next = new Map(prev);
      const to: MediaView = viewOf(id) === 'compact' ? 'expanded' : 'compact';
      if (to === mediaView) next.delete(id);
      else next.set(id, to);
      return next;
    });
  useEffect(() => setFlipped(new Map()), [accountId, fanId, mediaView]);

  // Stay at the bottom for new messages; keep the view still when older ones load above.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const first = messages[0]?.id ?? null;
    if (prevFirst.current && first !== prevFirst.current && !pinnedToBottom.current) {
      el.scrollTop += el.scrollHeight - prevHeight.current;
    } else if (pinnedToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
    prevFirst.current = first;
    prevHeight.current = el.scrollHeight;
  }, [messages]);

  useEffect(() => {
    pinnedToBottom.current = true;
    prevFirst.current = null;
  }, [accountId, fanId]);

  useEffect(() => {
    const el = topSentinel.current;
    if (!el || !hasOlder) return;
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && onLoadOlder(), {
      root: scroller.current,
      rootMargin: '200px 0px 0px 0px',
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasOlder, onLoadOlder, messages.length]);

  useEffect(() => {
    if (!highlightId) return;
    const row = scroller.current?.querySelector(`[data-message-id="${CSS.escape(highlightId)}"]`);
    row?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [highlightId, messages]);

  // Fan messages coming into view (auto-translate translates what is read, not the whole history).
  useEffect(() => {
    const el = scroller.current;
    if (!el || !onSeen) return;
    const byId = new Map(messages.filter((m) => m.side === 'fan' && m.text && !m.pending).map((m) => [m.id, m]));
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          io.unobserve(entry.target);
          const m = byId.get((entry.target as HTMLElement).dataset.messageId ?? '');
          if (m) onSeen(m);
        }
      },
      { root: el, rootMargin: '200px 0px' },
    );
    el.querySelectorAll<HTMLElement>('[data-message-id]').forEach((row) => byId.has(row.dataset.messageId ?? '') && io.observe(row));
    return () => io.disconnect();
  }, [messages, onSeen]);

  // Phones: the message held down (its actions sheet is open).
  const [held, setHeld] = useState<ChatMessage | null>(null);
  const hold = useRef<{ id: number; x: number; y: number; timer: number } | null>(null);
  useEffect(() => setHeld(null), [accountId, fanId]);
  const stopHold = () => {
    if (hold.current) window.clearTimeout(hold.current.timer);
    hold.current = null;
  };
  useEffect(() => stopHold, []);
  const holdHandlers = (m: ChatMessage) =>
    holdToAct && !m.pending
      ? {
          onPointerDown: (e: React.PointerEvent) => {
            if (e.button !== 0 || (e.target as Element).closest(NOT_A_HOLD)) return;
            stopHold();
            const timer = window.setTimeout(() => {
              hold.current = null;
              navigator.vibrate?.(10);
              setHeld(m);
            }, HOLD_MS);
            hold.current = { id: e.pointerId, x: e.clientX, y: e.clientY, timer };
          },
          onPointerMove: (e: React.PointerEvent) => {
            const h = hold.current;
            if (h && h.id === e.pointerId && Math.hypot(e.clientX - h.x, e.clientY - h.y) > HOLD_SLOP) stopHold();
          },
          onPointerUp: stopHold,
          onPointerCancel: stopHold,
          onPointerLeave: stopHold,
          // Android and desktop browsers say "long press" as a context menu.
          onContextMenu: (e: React.MouseEvent) => {
            if ((e.target as Element).closest(NOT_A_HOLD)) return;
            e.preventDefault();
            stopHold();
            setHeld(m);
          },
        }
      : {};

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    pinnedToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    prevHeight.current = el.scrollHeight;
  };

  return (
    <div className={`rc-thread ${holdToAct ? 'is-hold' : ''}`} ref={scroller} onScroll={onScroll}>
      <div className="rc-thread-inner">
        <div ref={topSentinel} className="rc-thread-top">
          {loadingOlder && <Spinner size={16} label={t('thread.loadingOlder')} />}
          {!hasOlder && !loading && messages.length > 0 && <span className="rc-muted">{t('thread.start')}</span>}
        </div>
        {loading && <LoadingBlock pad="40px 0" label={t('thread.loading')} />}
        {error && !loading && <div className="rc-thread-state rc-thread-state--error">{t('thread.error', { error })}</div>}
        {!loading && !error && messages.length === 0 && <div className="rc-thread-state">{emptyText ?? t('thread.empty')}</div>}
        {messages.map((m, i) => {
          const prev = messages[i - 1];
          const day = new Date(m.createdAt).toDateString();
          const newDay = !prev || new Date(prev.createdAt).toDateString() !== day;
          const label = dayLabel(m.createdAt, i18n.language);
          const sameSender = prev && !newDay && prev.side === m.side && JSON.stringify(prev.sender) === JSON.stringify(m.sender);
          const mine = m.side === 'creator';
          const next = messages[i + 1];
          const lastOfGroup = !next || next.side !== m.side || JSON.stringify(next.sender) !== JSON.stringify(m.sender) || new Date(next.createdAt).toDateString() !== day;
          // Content messages carry their price and send time at the bottom of the bubble.
          const priced = !!priceState(m);
          const seen = mine && m.isRead;
          // Every sent message names its sender under the bubble (in the time line when it has one).
          const byline = mine && !m.pending && knownSender(m);
          const meta = lastOfGroup && !m.pending && (!priced || seen);
          return (
            <Fragment key={m.id}>
              {newDay && (
                <div className="rc-day">
                  <span>{label === 'yesterday' ? t('thread.yesterday') : label || t('thread.today')}</span>
                </div>
              )}
              {!sameSender && (!mine ? <span className="rc-sender">{fanName}</span> : <span className="rc-sender-gap" />)}
              <div
                className={`rc-msg ${mine ? 'is-mine' : ''} ${m.pending ? `is-${m.pending.state}` : ''} ${m.id === highlightId ? 'is-hit' : ''} ${held?.id === m.id ? 'is-held' : ''}`}
                data-message-id={m.id}
                {...holdHandlers(m)}
              >
                {!m.pending && !holdToAct && <MessageMenu m={m} onReply={onReply} onCopyToSend={onCopyToSend} onUnsend={onUnsend} onLike={onLike} onTranslate={onTranslate} />}
                {m.liked && <LikeHeart m={m} onLike={onLike} />}
                {m.tip && (
                  <span className="rc-tip">
                    <span className="rc-tip-ic">
                      <FeatherIcon name="circle-dollar-sign" size={15} />
                    </span>
                    <b>{money(m.tip.amount)}</b>
                    <span className="rc-muted">{t('thread.tip')}</span>
                  </span>
                )}
                {m.media.length > 0 &&
                  (m.media.every((item) => item.type === 'audio') ? (
                    <div className="rc-media-grid">
                      {m.media.map((item, idx) => (
                        <Thumb key={`${item.id}:${idx}`} accountId={accountId} fanId={fanId} item={item} />
                      ))}
                    </div>
                  ) : viewOf(m.id) === 'compact' ? (
                    <MediaChip accountId={accountId} items={m.media} onExpand={() => flip(m.id)} />
                  ) : (
                    <div className={`rc-media-grid ${m.media.length > 1 ? 'is-multi' : ''}`}>
                      {m.media.map((item, idx) => (
                        <Thumb key={`${item.id}:${idx}`} accountId={accountId} fanId={fanId} item={item} />
                      ))}
                      <button type="button" className="rc-media-flip" title={t('media.shorten')} aria-label={t('media.shorten')} onClick={() => flip(m.id)}>
                        <FeatherIcon name="minimize-2" size={13} />
                      </button>
                    </div>
                  ))}
                {m.giphyId && <img className="rc-gif" src={`https://media.giphy.com/media/${encodeURIComponent(m.giphyId)}/giphy.gif`} alt="GIF" />}
                {m.text && <MessageText m={m} earlier={messages.slice(0, i)} fanName={fanName} />}
                <TranslationLine tr={translations?.get(m.id)} />
                {priced && (
                  <span className="rc-msg-foot">
                    <PriceState m={m} />
                    {!m.pending && <span className="rc-msg-foot-time">{timeLabel(m.createdAt, i18n.language)}</span>}
                  </span>
                )}
              </div>
              {m.pending?.state === 'sending' && (
                <span className="rc-meta is-mine rc-meta--pending">
                  <Spinner size={11} label={t('thread.sending')} />
                </span>
              )}
              {m.pending?.state === 'failed' && (
                <span className="rc-meta is-mine rc-meta--failed" title={m.pending.error}>
                  <FeatherIcon name="alert-circle" size={12} />
                  {t('thread.notSent')}
                  {onRetry && (
                    <button type="button" onClick={() => onRetry(m.id)}>
                      {t('thread.retry')}
                    </button>
                  )}
                  {onDiscard && (
                    <button type="button" onClick={() => onDiscard(m.id)}>
                      {t('thread.discard')}
                    </button>
                  )}
                </span>
              )}
              {byline && !meta && (
                <span className="rc-meta is-mine rc-meta--by">
                  <SenderChip m={m} />
                </span>
              )}
              {meta && (
                <span className={`rc-meta ${mine ? 'is-mine' : ''}`}>
                  {byline && <SenderChip m={m} />}
                  {byline && (!priced || seen) && ' · '}
                  {!priced && timeLabel(m.createdAt, i18n.language)}
                  {seen && (
                    <>
                      {!priced && ' · '}
                      {t('thread.seen')}
                      <FeatherIcon name="check-check" size={13} className="rc-seen" />
                    </>
                  )}
                </span>
              )}
            </Fragment>
          );
        })}
      </div>
      {holdToAct && (
        <MessageSheet m={held} onClose={() => setHeld(null)} onReply={onReply} onCopyToSend={onCopyToSend} onUnsend={onUnsend} onLike={onLike} onTranslate={onTranslate} />
      )}
    </div>
  );
}

/** A message's text; a reply's quote line drawn as a quote block, named when the quoted message is in the thread. */
function MessageText({ m, earlier, fanName }: { m: ChatMessage; earlier: ChatMessage[]; fanName: string }) {
  const { t } = useTranslation('chat');
  const { quote, body } = splitQuote(m.text);
  if (!quote) return <span className="rc-text">{m.text}</span>;
  const quoted = findQuoted(quote, earlier);
  return (
    <>
      <span className="rc-quote">
        {quoted && <span className="rc-quote-who">{quoted.side === 'creator' ? t('sender.you') : fanName}</span>}
        <span className="rc-quote-text">{quote}</span>
      </span>
      <span className="rc-text">{body}</span>
    </>
  );
}
